import { useCallback, useState } from 'react'

import type { Row } from '@tanstack/react-table'
import { useNavigate } from 'react-router'

import DuplicateIcon from '@/assets/icons/icon-content-copy.svg'
import EditIcon from '@/assets/icons/icon-create.svg'
import InfoIcon from '@/assets/icons/icon-description.svg'
import ActionMenuIcon from '@/assets/icons/icon-move-vert.svg'
import PauseIcon from '@/assets/icons/icon-pause.svg'
import StartIcon from '@/assets/icons/icon-play-outlined.svg'
import WithdrawIcon from '@/assets/icons/icon-settings-backup-restore.svg'
import StopIcon from '@/assets/icons/icon-stop.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'

import type { BulkMessageResponseContent } from '../types'

interface ActionButtonProps {
  row: Row<BulkMessageResponseContent>
  checkIfSomethingIsRunning: () => boolean
}

export const ActionButton = ({
  row,
  checkIfSomethingIsRunning,
}: ActionButtonProps) => {
  const status = row.original.message.status

  const [openMenu, setOpenMenu] = useState(false)

  const { multipleBulkMessagesAllowed } = useSettingsContext()
  const toast = useToast()

  const redirect = useNavigate()
  const handleRedirect = () => {
    redirect(`edit/${row.original.message.id}`)
  }
  const handleRedirectDuplication = () => {
    redirect(`edit/${row.original.message.id}?copy`)
  }
  const handleRedirectInfo = () => {
    redirect(`edit/${row.original.message.id}?info`)
  }

  const handleStop = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.STOP_OMM_DIALOG,
      messageId: row.original.message.id,
    })
  }, [row.original.message.id])

  const handlePause = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.PAUSE_OMM_DIALOG,
      messageId: row.original.message.id,
    })
  }, [row.original.message.id])

  const handleResume = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.RESUME_OMM_DIALOG,
      messageId: row.original.message.id,
    })
  }, [row.original.message.id])

  const handleWithdrawProgress = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.WITHDRAW_OMM_DIALOG,
      messageId: row.original.message.id,
    })
  }, [row.original.message.id])

  const handleOpenWithdrawConfirmation = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.RUN_OMM_WITHDRAW_DIALOG,
      onSuccess: handleWithdrawProgress,
    })
  }, [handleWithdrawProgress])

  const handleStart = useCallback(
    async () => {
      if (!multipleBulkMessagesAllowed && checkIfSomethingIsRunning()) {
        toast.error(
          "You can't send out an Online Mass Message while another one is still active.",
        )

        return
      }

      openDialog({
        type: DIALOG_TYPE.START_OMM_DIALOG,
        messageId: row.original.message.id,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkIfSomethingIsRunning, row.original.message.id],
  )

  return (
    <div className="relative flex gap-6">
      <DropdownMenu
        open={openMenu}
        onOpenChange={setOpenMenu}
      >
        <DropdownMenuTrigger className="cursor-pointer">
          <ActionMenuIcon className="fill-black dark:fill-white" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-40 p-1"
          forceMount
        >
          {status !== 'Cancelled' && status !== 'Done' && (
            <DropdownMenuItem onClick={handleRedirect}>
              <div className="flex flex-row items-center gap-2">
                <EditIcon />
                <span>Edit</span>
              </div>
            </DropdownMenuItem>
          )}

          {(status === 'Cancelled' || status === 'Done') && (
            <DropdownMenuItem onClick={handleRedirectInfo}>
              <div className="flex flex-row items-center gap-2">
                <InfoIcon />
                <span>Info</span>
              </div>
            </DropdownMenuItem>
          )}

          <DropdownMenuItem onClick={handleRedirectDuplication}>
            <div className="flex flex-row items-center gap-2">
              <DuplicateIcon />
              <span>Duplicate</span>
            </div>
          </DropdownMenuItem>

          {status === 'Pending' && (
            <DropdownMenuItem onClick={handleStart}>
              <div className="flex flex-row items-center gap-2">
                <StartIcon />
                <span>Start now</span>
              </div>
            </DropdownMenuItem>
          )}

          {status === 'Running' && (
            <DropdownMenuItem onClick={handlePause}>
              <div className="flex flex-row items-center gap-2">
                <PauseIcon />
                <span>Pause</span>
              </div>
            </DropdownMenuItem>
          )}

          {status === 'Paused' && (
            <DropdownMenuItem onClick={handleResume}>
              <div className="flex flex-row items-center gap-2">
                <StartIcon />
                <span>Resume</span>
              </div>
            </DropdownMenuItem>
          )}

          {(status === 'Pending' ||
            status === 'Running' ||
            status === 'Paused') && (
            <DropdownMenuItem onClick={handleStop}>
              <div className="flex flex-row items-center gap-2">
                <StopIcon />
                <span>{status === 'Running' ? 'Stop' : 'Cancel'}</span>
              </div>
            </DropdownMenuItem>
          )}

          {status !== 'Pending' && (
            <DropdownMenuItem onClick={handleOpenWithdrawConfirmation}>
              <div className="flex flex-row items-center gap-2">
                <WithdrawIcon />
                <span>Withdraw</span>
              </div>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
